(() => {
  if (customElements.get('fabbricantti-cart-coupon')) return;

  const root = window.Shopify?.routes?.root || '/';
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const states = new Map();
  const checkoutControls = new Map();
  let currentCart = null;
  let cartRequest = null;
  let busy = false;

  const codesOf = (cart) => (cart.discount_codes || []).map(({ code }) => code);
  const sameCode = (first, second) => first.toLocaleLowerCase() === second.toLocaleLowerCase();
  const readCart = async () => {
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error('Cart unavailable');
    return response.json();
  };

  const setBusy = (value) => {
    busy = value;
    for (const control of document.querySelectorAll('button[name="checkout"], input[name="checkout"], .additional-checkout-buttons')) {
      if (value) {
        if (!checkoutControls.has(control)) checkoutControls.set(control, { disabled: control.disabled, inert: control.inert });
        if ('disabled' in control) control.disabled = true;
        control.inert = true;
      }
    }
    if (!value) {
      for (const [control, previous] of checkoutControls) {
        if ('disabled' in control) control.disabled = previous.disabled;
        control.inert = previous.inert;
      }
      checkoutControls.clear();
    }
    document.querySelectorAll('fabbricantti-cart-coupon').forEach((element) => element.setBusy(value));
  };

  document.addEventListener('submit', (event) => {
    if (busy && event.target.matches('#mini-cart-form, .cart__recap')) event.preventDefault();
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.cart-coupon__remove[data-remove-code]');
    if (!button) return;
    const container = button.closest('cart-drawer, .shopify-section');
    container?.querySelector('fabbricantti-cart-coupon')?.update(button.dataset.removeCode);
  });

  document.documentElement.addEventListener('cart:updated', (event) => {
    currentCart = event.detail.cart;
    document.querySelectorAll('fabbricantti-cart-coupon').forEach((element) => element.renderCodes());
  });

  class CartCoupon extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.button = this.querySelector('.cart-coupon__apply');
      this.message = this.querySelector('.cart-coupon__message');
      this.toggle = this.querySelector('.cart-coupon__toggle');
      this.panel = this.querySelector('.cart-coupon__body');
      this.container = this.closest('cart-drawer, .shopify-section') || this.parentElement;
      this.drawer = this.closest('cart-drawer');
      this.applyLabel = this.button.textContent;
      this.minimizedKey = `fabbricantti-cart-coupon-minimized-${this.dataset.context}`;
      let minimized = false;
      try { minimized = sessionStorage.getItem(this.minimizedKey) === 'true'; } catch {}
      this.state = states.get(this.dataset.context) || { draft: '', message: '', error: false, minimized: this.drawer ? true : minimized };
      if (this.drawer && !this.drawer.hasAttribute('open')) this.state.minimized = true;
      states.set(this.dataset.context, this.state);
      this.input.value = this.state.draft;
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.toggle.addEventListener('click', () => {
        this.setMinimized(this.toggle.getAttribute('aria-expanded') === 'true');
      }, options);
      this.button.addEventListener('click', () => this.update(), options);
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          this.update();
        }
      }, options);
      this.input.addEventListener('input', () => {
        this.state.draft = this.input.value;
        this.showMessage('', false);
      }, options);
      this.setBusy(busy);
      this.showMessage(this.state.message, this.state.error);
      this.renderCodes();
      this.setMinimized(Boolean(this.state.minimized), false);
      if (this.drawer) {
        this.drawerObserver = new MutationObserver((records) => {
          if (this.drawer.hasAttribute('open') && records.some((record) => record.oldValue === null)) {
            this.setMinimized(true, false);
          }
        });
        this.drawerObserver.observe(this.drawer, { attributes: true, attributeFilter: ['open'], attributeOldValue: true });
        this.listeners.signal.addEventListener('abort', () => this.drawerObserver.disconnect(), { once: true });
      }
      if (!currentCart) {
        cartRequest ||= readCart().finally(() => { cartRequest = null; });
        cartRequest.then((cart) => {
          if (!currentCart) currentCart = cart;
          if (this.isConnected) this.renderCodes();
        }).catch(() => {});
      }
    }

    disconnectedCallback() {
      this.collapseAnimation?.cancel();
      this.listeners?.abort();
    }

    setMinimized(value, animate = true) {
      const startHeight = this.panel.getBoundingClientRect().height;
      this.collapseAnimation?.cancel();
      this.collapseAnimation = null;
      this.state.minimized = value;
      this.toggle.setAttribute('aria-expanded', String(!value));
      this.toggle.setAttribute('aria-label', value ? this.toggle.dataset.expandLabel : this.toggle.dataset.minimizeLabel);
      this.panel.toggleAttribute('inert', value);
      this.panel.setAttribute('aria-hidden', String(value));
      const finish = () => {
        this.panel.hidden = value;
        this.panel.style.overflow = '';
      };
      if (animate && this.panel.animate && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.panel.hidden = false;
        this.panel.style.overflow = 'hidden';
        const animation = this.panel.animate({
          height: [`${startHeight}px`, `${value ? 0 : this.panel.scrollHeight}px`]
        }, { duration: 500, easing: 'cubic-bezier(0.75, 0, 0.175, 1)', fill: 'both' });
        this.collapseAnimation = animation;
        animation.onfinish = () => {
          if (this.collapseAnimation !== animation) return;
          finish();
          animation.cancel();
          this.collapseAnimation = null;
        };
      } else finish();
      try { sessionStorage.setItem(this.minimizedKey, String(value)); } catch {}
    }

    setBusy(value) {
      this.input.disabled = value;
      this.button.disabled = value;
      if (value) {
        const label = document.createElement('span');
        label.textContent = this.dataset.applying.replace(/\.+$/, '');
        const dots = document.createElement('span');
        dots.className = 'cart-coupon__dots';
        dots.setAttribute('aria-hidden', 'true');
        this.button.replaceChildren(label, dots);
        this.button.setAttribute('aria-label', this.dataset.applying);
      } else {
        this.button.textContent = this.applyLabel;
        this.button.removeAttribute('aria-label');
      }
      this.setAttribute('aria-busy', String(value));
      this.container.querySelectorAll('.cart-coupon__remove').forEach((button) => { button.disabled = value; });
    }

    showMessage(message, error) {
      this.state.message = message;
      this.state.error = error;
      this.message.textContent = message;
      this.message.hidden = !message;
      this.message.toggleAttribute('data-error', error);
      if (error) this.input.setAttribute('aria-invalid', 'true');
      else this.input.removeAttribute('aria-invalid');
    }

    renderCodes() {
      this.container.querySelectorAll('.cart-coupon__remove, [data-coupon-extra]').forEach((element) => element.remove());
      for (const { code, applicable } of currentCart?.discount_codes || []) {
        if (!applicable) continue;
        let badge = [...this.container.querySelectorAll('.mini-cart__discount-badge, .cart__discount-badge, .line-item__discount-badge')]
          .find((element) => sameCode(element.textContent.trim(), code));
        if (!badge) {
          const list = document.createElement('ul');
          list.className = 'mini-cart__discount-list list--unstyled';
          list.setAttribute('data-coupon-extra', '');
          const item = document.createElement('li');
          item.className = 'mini-cart__discount';
          badge = document.createElement('span');
          badge.className = 'mini-cart__discount-badge discount-badge';
          badge.textContent = code;
          item.append(badge);
          list.append(item);
          this.before(list);
        }
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'cart-coupon__remove';
        remove.dataset.removeCode = code;
        remove.setAttribute('aria-label', `${this.dataset.removeLabel} ${code}`);
        remove.textContent = '×';
        remove.disabled = busy;
        badge.append(remove);
      }
    }

    async update(removeCode) {
      if (busy) return;
      const code = this.input.value.trim();
      if (!removeCode && (!code || code.includes(','))) {
        this.showMessage(code ? this.dataset.invalid : this.dataset.empty, true);
        this.input.focus();
        return;
      }
      this.state.draft = this.input.value;
      this.showMessage('', false);
      const loadingStarted = performance.now();
      const finishLoading = async () => {
        const remaining = 900 - (performance.now() - loadingStarted);
        if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining));
      };
      setBusy(true);
      let updatedCart = null;
      try {
        const before = await readCart();
        const previousCodes = codesOf(before);
        const desiredCodes = removeCode
          ? previousCodes.filter((existing) => !sameCode(existing, removeCode))
          : [...previousCodes.filter((existing) => !sameCode(existing, code)), code];
        const sections = [...new Set(['mini-cart', this.dataset.sectionId])];
        const writeCart = async (codes) => {
          const response = await fetch(`${root}cart/update.js`, {
            method: 'POST', headers,
            body: JSON.stringify({ discount: codes.join(','), sections, sections_url: location.pathname })
          });
          if (!response.ok) throw new Error('Discount unavailable');
          return response.json();
        };
        updatedCart = await writeCart(desiredCodes);
        const accepted = removeCode || updatedCart.discount_codes?.some((discount) => sameCode(discount.code, code) && discount.applicable);
        if (!accepted) {
          updatedCart = await writeCart(previousCodes);
          await finishLoading();
          this.showMessage(this.dataset.invalid, true);
        } else {
          await finishLoading();
          this.state.draft = '';
          this.input.value = '';
          this.showMessage(removeCode ? this.dataset.removed : this.dataset.applied, false);
        }
      } catch {
        await finishLoading();
        this.showMessage(this.dataset.unavailable, true);
      }
      if (updatedCart) {
        currentCart = updatedCart;
        await this.refresh(updatedCart);
      }
      setBusy(false);
    }

    async refresh(cart) {
      if (this.dataset.context === 'page') {
        const sectionId = this.dataset.sectionId;
        let html = cart.sections?.[sectionId];
        try {
          if (!html) {
            const url = new URL(location.href);
            url.searchParams.set('section_id', sectionId);
            const response = await fetch(url);
            if (!response.ok) throw new Error('Section unavailable');
            html = await response.text();
          }
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const replacement = parsed.getElementById(`shopify-section-${sectionId}`);
          const original = document.getElementById(`shopify-section-${sectionId}`);
          if (!replacement || !original) throw new Error('Section unavailable');
          original.innerHTML = replacement.innerHTML;
        } catch {
          location.reload();
          return;
        }
      }
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
      const drawerCart = cart.sections?.['mini-cart'] ? cart : null;
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: drawerCart, replacementDelay: 0 } }));
    }
  }

  customElements.define('fabbricantti-cart-coupon', CartCoupon);
})();
